import { useLocation, useNavigate } from "react-router-dom";
import FlightCard from "./FlightCard";
import FlightFilterSidebar from "./FlightFilterbar";
import FlightSearchBar from "./FlightSearchbar";
import RoundTripFlightCard from "./RoundTripFlightCard";
import MultiCityFlightCard from "./MultiCityFlightCard";
import { useEffect, useMemo, useRef, useState } from "react";
import { shouldShowAlternateItinerary } from "../../utils/passengerUtils";
import { useSearch } from "../../context/SearchContext";
import FareCalendar from "./FareCalendar";
import DatePriceStrip from "./DatePriceStrip";
import {
  FaPlane, FaSlidersH, FaSearch, FaTimes, FaSort,
  FaBolt, FaTag, FaFilter, FaChevronRight, FaCalendarAlt,
} from "react-icons/fa";

const airlineNames: Record<string, string> = {
  "6E": "Indigo", AI: "Air India", QR: "Qatar Airways",
  EY: "Etihad", EK: "Emirates", QP: "Akasa Air",
  SG: "Spice Jet", G9: "Air Arabia", GF: "Gulf Air",
  KU: "Kuwait Airways", RJ: "Royal Jordanian", ME: "Middle East Airlines",
  MS: "EgyptAir", BG: "Biman Bangladesh Airlines", PC: "Pegasus Airlines",
  XY: "Flynas", SV: "Saudia", PK: "Pakistan International Airlines",
  WY: "Oman Airlines",
};

const FlightResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { origin: userOrigin, destination: userDestination, updateSearchParams } = useSearch();
  const {
    from, to, selectedFrom, selectedTo,
    tripType = "roundtrip", departureDate, returnDate,
    selectedClass, classOfService,
    showDirectOnly: initialShowDirectOnly,
    passengerCounts, segments: initialSegments,
  } = location.state || {};

  const backendData = location.state?.flights || {};
  const simplifiedData = backendData.simplifiedData || [];
  const groupsMeta = backendData.groupsMeta || [];
  const searchSessionId = backendData.searchSessionId;

  const effectiveUserOrigin =
    (userOrigin ?? selectedFrom?.code ?? from ?? "").toString().toUpperCase();
  const effectiveUserDestination =
    (userDestination ?? selectedTo?.code ?? to ?? "").toString().toUpperCase();

  const mergedFlights = simplifiedData.map((flight: any, i: number) => {
    const groupIndex = flight.groupIndex ?? 0;
    return {
      ...flight,
      _origIndex: i,
      _uniqueKey: `${groupIndex}-${flight.id}-${i}`,
      groupIndex,
      groupLabel: flight.groupLabel,
      onwardBrandedFares: flight.onwardBrandedFares ?? [],
      returnBrandedFares: flight.returnBrandedFares ?? [],
      searchSessionId,
    };
  });

  const [results, setResults] = useState<any[]>([]);
  const [isRepricing, setIsRepricing] = useState(false);
  const [showDirectOnly, setShowDirectOnly] = useState(initialShowDirectOnly ?? false);
  const [sortOption, setSortOption] = useState<"none" | "cheapest" | "fastest">("none");
  const [selectedStops, setSelectedStops] = useState<string[]>([]);
  const [selectedDepartureTimes, setSelectedDepartureTimes] = useState<string[]>([]);
  const [selectedAirlines, setSelectedAirlines] = useState<string[]>([]);
  const [duration, setDuration] = useState(4);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showFareCalendar, setShowFareCalendar] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showMobileFilter, setShowMobileFilter] = useState(false);

  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const mobileFilterRef = useRef<HTMLDivElement>(null);
  const mobileSummaryRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const [stickyTopPx, setStickyTopPx] = useState(180);
  const [mobileStickyTopPx, setMobileStickyTopPx] = useState(120);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setIsEditMode(false);
  }, [location.state?.flights]);

  useEffect(() => {
    setResults(mergedFlights);
  }, [location.state]);

  useEffect(() => {
    const measure = () => {
      const el = mobileSummaryRef.current;
      if (el && window.innerWidth < 640 && window.getComputedStyle(el).display !== "none") {
        const h = el.offsetHeight;
        if (h > 0) setMobileStickyTopPx(h + 8);
      }
    };
    measure();
    const t = setTimeout(measure, 100);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
  }, []);

  useEffect(() => {
    const measure = () => {
      const el = searchBarRef.current;
      if (el && window.getComputedStyle(el).display !== "none") {
        const h = el.offsetHeight;
        if (h > 0) setStickyTopPx(h + 16);
      }
    };
    measure();
    const t = setTimeout(measure, 100);
    window.addEventListener("resize", measure);
    let ro: ResizeObserver | null = null;
    const t2 = setTimeout(() => {
      const el = searchBarRef.current;
      if (el) { ro = new ResizeObserver(measure); ro.observe(el); }
    }, 150);
    return () => {
      clearTimeout(t); clearTimeout(t2);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [location.state]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (showMobileSearch && mobileSearchRef.current && !mobileSearchRef.current.contains(event.target as Node))
        setShowMobileSearch(false);
      if (showMobileFilter && mobileFilterRef.current && !mobileFilterRef.current.contains(event.target as Node))
        setShowMobileFilter(false);
    }
    if (showMobileSearch || showMobileFilter)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMobileSearch, showMobileFilter]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const parseTimeFromISO = (dt?: string): string => {
    if (!dt) return "-";
    const m = dt.match(/T(\d{2}:\d{2})/);
    return m ? m[1] : dt;
  };

  const cabinClassNameToCode: Record<string, string> = {
    economy: "Y", premiumeconomy: "S", business: "C",
    premiumbusiness: "J", first: "F", premiumfirst: "P",
  };

  const mapFareOption = (fo: any, firstSegment?: any) => {
    const rawCabinCode: string | undefined = firstSegment?.bookingInfo?.cabinCode ?? undefined;
    const rawCabinClass: string | undefined = fo.cabinClass ?? undefined;
    const resolvedCabinCode =
      rawCabinCode ??
      (rawCabinClass ? cabinClassNameToCode[rawCabinClass.toLowerCase().replace(/\s+/g, "")] : undefined);
    return {
      label: fo.name,
      price: fo.passengerPrices?.find((p: any) => p.type === "ADT")?.total ?? fo.price?.total,
      currency: fo.price?.currency,
      baggage: fo.baggage?.checked?.description ?? fo.baggage?.cabin?.description ?? "",
      cancellation:
        fo.refundPolicy?.conditions ??
        (fo.refundPolicy?.refundable
          ? "Refundable"
          : fo.refundPolicy?.cancellationFee
            ? `Non-refundable (fee: ${fo.refundPolicy.cancellationFee.amount} ${fo.refundPolicy.cancellationFee.currency})`
            : "Non-refundable"),
      addOns: fo.amenities?.map((a: any) => a.description).join(", ") ?? "",
      cabinCode: resolvedCabinCode,
      fareBasisCode: fo.fareBasis?.code,
      fareType: fo.fareBasis?.type,
      bookingCodes: fo.bookingClasses,
      brandFeatures: fo.amenities?.map((a: any, i: number) => ({ id: i, commercialName: a.description })),
      fullGroup: fo,
    };
  };

  const availableAirlines = useMemo(() => {
    const codes = new Set<string>();
    results.forEach((flight) => {
      const code = flight.validatingCarrier?.code ?? flight.validatingCarrier;
      if (code) codes.add(code);
    });
    return Array.from(codes).map((code) => ({ code, name: airlineNames[code] || code }));
  }, [results]);

  const isMulticityFlight = (flight: any) =>
    tripType === "multicity" ||
    flight.tripType === "multicity" ||
    (Array.isArray(flight.legs) && flight.legs.some((l: any) => l.direction?.startsWith("segment_")));

  const stopsLabel = (stops: number | undefined) => {
    if (stops === undefined) return "";
    if (stops === 0) return "0";
    if (stops === 1) return "1";
    if (stops > 2) return "More than 2";
    return String(stops);
  };

  const resolveFlightType = (
    tt: "oneway" | "roundtrip",
    a: number, c: number, i: number
  ): string => {
    const isRet = tt === "roundtrip";
    if (a && c && i) return isRet ? "ADTCHILDINFRETURN" : "ADTCHILDINF1WAY";
    if (a && c) return isRet ? "ADTCHILDRETURN" : "ADTCHILD1WAY";
    if (a && i) return isRet ? "ADTINFRETURN" : "ADTINF1WAY";
    return isRet ? "ADTRETURN" : "ADT1WAY";
  };

  const handleStripDateChange = async (newDate: string) => {
    if (!selectedFrom?.code || !selectedTo?.code) return;
    if (tripType === "multicity") return;
    setIsRepricing(true);
    try {
      const adt = passengerCounts?.ADT ?? 1;
      const chd = passengerCounts?.CHD ?? 0;
      const inf = passengerCounts?.INF ?? 0;
      const flightType = resolveFlightType(
        tripType as "oneway" | "roundtrip",
        adt, chd, inf
      );
      const payload = {
        data: {
          flightType,
          departureDate: newDate,
          returnDate: tripType === "roundtrip" ? returnDate : undefined,
          from: selectedFrom.code,
          to: selectedTo.code,
          passengerQuantities: { ADT: adt, CHD: chd, INF: inf },
        },
        classOfService: classOfService || "Y",
        currency: "AED",
      };
      const res = await fetch(`${process.env.REACT_APP_API_URL}/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.error || "Search failed");

      // Keep the global search context in sync with the new date so the
      // search bar, fare calendar, and any other consumer show the right value.
      updateSearchParams({
        from: selectedFrom.code,
        to: selectedTo.code,
        departureDate: newDate,
        ...(tripType === "roundtrip" && returnDate ? { returnDate } : {}),
      });

      navigate("/results", {
        state: {
          ...location.state,
          flights: result,
          departureDate: newDate,
        },
        replace: true,
      });
    } catch (err) {
      console.error("[DateStrip] reprice failed:", err);
    } finally {
      setIsRepricing(false);
    }
  };

  const getDepartureTimeSlot = (timeStr: string) => {
    if (!timeStr) return null;
    const hour = parseInt(timeStr.split(":")[0], 10);
    if (hour < 12) return "00 - 12";
    if (hour < 16) return "12 - 16";
    if (hour < 20) return "16 - 20";
    return "20 - 24";
  };

  const filteredFlights = results.filter((flight) => {
    const isMulticity = isMulticityFlight(flight);
    const flightLegs: any[] = flight.legs ?? [];
    const outboundLeg = flightLegs.find((l: any) => l.direction === "outbound") ?? flightLegs[0];
    const inboundLeg = flightLegs.find((l: any) => l.direction === "inbound");
    const carrierCode = flight.validatingCarrier?.code ?? flight.validatingCarrier;

    if (showDirectOnly) {
      if (isMulticity) {
        if (flightLegs.length === 0) return false;
        if (!flightLegs.every((leg: any) => (leg.stops ?? 0) === 0)) return false;
      } else {
        const onwardStop = (outboundLeg?.stops ?? 0) === 0;
        const returnStop = tripType === "roundtrip" ? (inboundLeg?.stops ?? 0) === 0 : true;
        if (!(onwardStop && returnStop)) return false;
      }
    }

    if (selectedStops.length > 0) {
      if (isMulticity) {
        const allowedLabels = new Set<string>();
        if (selectedStops.includes("0")) allowedLabels.add("0");
        if (selectedStops.includes("1")) { allowedLabels.add("0"); allowedLabels.add("1"); }
        if (selectedStops.includes("More than 2")) allowedLabels.add("More than 2");
        if (!flightLegs.some((leg: any) => allowedLabels.has(stopsLabel(leg.stops)))) return false;
      } else {
        if (!selectedStops.includes(stopsLabel(outboundLeg?.stops))) return false;
        if (tripType === "roundtrip" && !selectedStops.includes(stopsLabel(inboundLeg?.stops))) return false;
      }
    }

    if (selectedAirlines.length > 0 && !selectedAirlines.includes(carrierCode)) return false;

    if (duration > 4 && duration <= 20) {
      if (isMulticity) {
        if (flightLegs.some((leg: any) => (leg.duration ?? 0) / 60 > duration)) return false;
      } else if ((outboundLeg?.duration ?? 0) / 60 > duration) return false;
    }

    if (selectedDepartureTimes.length > 0) {
      const firstLeg = isMulticity ? flightLegs[0] : outboundLeg;
      const rawDT =
        firstLeg?.departure?.dateTime ||
        firstLeg?.segments?.[0]?.departure?.dateTime ||
        firstLeg?.departure?.time ||
        firstLeg?.segments?.[0]?.departure?.time ||
        flight?.onwardFlight?.schedules?.[0]?.departure?.time;
      let depTime: string | undefined;
      if (rawDT) {
        const m = String(rawDT).match(/T?(\d{2}:\d{2})/);
        depTime = m ? m[1] : undefined;
      }
      if (!depTime) return false; // can't classify → exclude when a filter is on
      const slot = getDepartureTimeSlot(depTime);
      if (slot && !selectedDepartureTimes.includes(slot)) return false;
    }
    return true;
  });

  const totalElapsed = (flight: any) => {
    if (Array.isArray(flight.legs) && flight.legs.length > 0)
      return (flight.legs as any[]).reduce((sum: number, leg: any) => sum + (leg.duration ?? 0), 0);
    return Number.MAX_SAFE_INTEGER;
  };

  const getAmountNumber = (flight: any): number => {
    const raw = flight?.fareOptions?.[0]?.price?.total ?? flight?.pricingInfo?.total;
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) ? n : Infinity;
  };

  const cheapestMeta = useMemo(() => {
    if (filteredFlights.length === 0) return null;
    let cheapest = filteredFlights[0];
    for (const f of filteredFlights) {
      if (getAmountNumber(f) < getAmountNumber(cheapest)) cheapest = f;
    }
    const amount = getAmountNumber(cheapest) === Infinity ? undefined : getAmountNumber(cheapest);
    return { amount, currency: cheapest?.fareOptions?.[0]?.price?.currency ?? cheapest?.pricingInfo?.currency };
  }, [filteredFlights]);

  const fastestMeta = useMemo(() => {
    if (filteredFlights.length === 0) return null;
    let fastest = filteredFlights[0];
    for (const f of filteredFlights) {
      if (totalElapsed(f) < totalElapsed(fastest)) fastest = f;
    }
    return {
      amount: fastest.pricingInfo?.total,
      currency: fastest.pricingInfo?.currency,
      elapsed: totalElapsed(fastest),
    };
  }, [filteredFlights]);

  const flightsToDisplay = useMemo(() => {
    if (sortOption === "none")
      return [...filteredFlights].sort((a, b) => (a._origIndex ?? 0) - (b._origIndex ?? 0));
    const arr = [...filteredFlights];
    if (sortOption === "cheapest") arr.sort((a, b) => getAmountNumber(a) - getAmountNumber(b));
    else if (sortOption === "fastest") arr.sort((a, b) => totalElapsed(a) - totalElapsed(b));
    return arr;
  }, [filteredFlights, sortOption]);

  const formatFare = (amount?: number | string, currency?: string) => {
    if (amount == null) return "-";
    const n = typeof amount === "number" ? amount : Number(amount);
    if (!Number.isFinite(n)) return "-";
    return `${currency || ""} ${n.toLocaleString()}`;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const totalPax =
    (passengerCounts?.ADT ?? 1) +
    (passengerCounts?.CHD ?? 0) +
    (passengerCounts?.INF ?? 0);

  const activeFilterCount =
    selectedStops.length +
    selectedDepartureTimes.length +
    selectedAirlines.length +
    (showDirectOnly ? 1 : 0);

  // ── Sort tile component ────────────────────────────────────────────────────
  const SortTile = ({
    id, icon, label, subLabel, active, onClick,
  }: {
    id: "cheapest" | "fastest";
    icon: React.ReactNode;
    label: string;
    subLabel: string;
    active: boolean;
    onClick: () => void;
  }) => (
    <button
      aria-pressed={active}
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all text-left min-w-[140px] ${active
          ? "bg-primary border-primary text-white shadow-md"
          : "bg-white border-gray-200 text-gray-700 hover:border-primary hover:bg-blue-50"
        }`}
    >
      <span className={`text-base ${active ? "text-white" : "text-primary"}`}>{icon}</span>
      <div>
        <div className="text-xs font-semibold leading-tight">{label}</div>
        <div className={`text-xs leading-tight mt-0.5 ${active ? "text-blue-100" : "text-gray-400"}`}>
          {subLabel}
        </div>
      </div>
    </button>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F2F6FF]">

      {/* ── Top header bar (desktop search) ── */}
      <div
        ref={searchBarRef}
        className="hidden sm:block sticky top-0 z-[60] bg-primary shadow-lg"
      >
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6">
          <FlightSearchBar
            showDirectOnly={showDirectOnly}
            setShowDirectOnly={setShowDirectOnly}
            from={from}
            to={to}
            selectedFrom={selectedFrom}
            selectedTo={selectedTo}
            departureDate={departureDate}
            returnDate={returnDate}
            selectedClass={selectedClass}
            tripType={tripType}
            passengerCounts={passengerCounts}
            isEditMode={isEditMode}
            onToggleEditMode={() => setIsEditMode(!isEditMode)}
            initialSegments={initialSegments}
          />
        </div>
      </div>

      {/* ── Mobile summary bar ── */}
      <div
        ref={mobileSummaryRef}
        className="sm:hidden sticky top-0 z-[60] bg-primary px-4 py-3 shadow-lg"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-white">
              <span className="font-bold text-base">
                {selectedFrom?.label ?? selectedFrom?.name ?? from ?? effectiveUserOrigin}
              </span>
              <FaChevronRight size={10} className="text-blue-200" />
              <span className="font-bold text-base">
                {selectedTo?.label ?? selectedTo?.name ?? to ?? effectiveUserDestination}
              </span>
            </div>
            <div className="text-blue-200 text-xs mt-0.5 flex items-center gap-1.5">
              <span>{formatDate(departureDate)}</span>
              {tripType === "roundtrip" && returnDate && (
                <><span>→</span><span>{formatDate(returnDate)}</span></>
              )}
              <span>·</span>
              <span>{totalPax} Pax</span>
              <span>·</span>
              <span>{selectedClass || classOfService}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              aria-label="Edit search"
              onClick={() => { setShowMobileSearch(true); setShowMobileFilter(false); }}
              className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
            >
              <FaSearch className="text-white text-sm" />
            </button>
            <button
              aria-label="Filter"
              onClick={() => { setShowMobileFilter(true); setShowMobileSearch(false); }}
              className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors relative"
            >
              <FaFilter className="text-white text-sm" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-orange-400 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile: edit search overlay ── */}
      {showMobileSearch && (
        <div
          className="sm:hidden fixed inset-0 z-[70] bg-black/50"
          onClick={() => setShowMobileSearch(false)}
        >
          <div
            ref={mobileSearchRef}
            className="bg-primary p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-semibold text-sm">Edit Search</span>
              <button
                onClick={() => setShowMobileSearch(false)}
                className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center"
              >
                <FaTimes className="text-white text-xs" />
              </button>
            </div>
            <FlightSearchBar
              showDirectOnly={showDirectOnly}
              setShowDirectOnly={setShowDirectOnly}
              from={from} to={to}
              selectedFrom={selectedFrom} selectedTo={selectedTo}
              departureDate={departureDate} returnDate={returnDate}
              selectedClass={selectedClass} tripType={tripType}
              passengerCounts={passengerCounts}
              isEditMode={true}
              onToggleEditMode={() => setShowMobileSearch(false)}
              initialSegments={initialSegments}
            />
          </div>
        </div>
      )}

      {/* ── Mobile: filter drawer ── */}
      {showMobileFilter && (
        <div
          className="sm:hidden fixed inset-0 z-[70] bg-black/50"
          onClick={() => setShowMobileFilter(false)}
        >
          <div
            ref={mobileFilterRef}
            className="absolute right-0 top-0 bottom-0 w-[85vw] max-w-sm bg-white overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-primary px-4 py-3 flex items-center justify-between">
              <span className="text-white font-semibold text-sm flex items-center gap-2">
                <FaSlidersH size={14} /> Filters
                {activeFilterCount > 0 && (
                  <span className="bg-orange-400 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </span>
              <button
                onClick={() => setShowMobileFilter(false)}
                className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center"
              >
                <FaTimes className="text-white text-xs" />
              </button>
            </div>
            <div className="p-4">
              <FlightFilterSidebar
                selectedStops={selectedStops}
                setSelectedStops={setSelectedStops}
                selectedDepartureTimes={selectedDepartureTimes}
                setSelectedDepartureTimes={setSelectedDepartureTimes}
                selectedAirlines={selectedAirlines}
                setSelectedAirlines={setSelectedAirlines}
                duration={duration}
                setDuration={setDuration}
                airlines={availableAirlines}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Date Price Strip (+/- 3 days around selected departure) ── */}
      {effectiveUserOrigin && effectiveUserDestination && tripType !== "multicity" && typeof departureDate === "string" && departureDate && (
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 pt-4">
          <DatePriceStrip
            origin={effectiveUserOrigin}
            destination={effectiveUserDestination}
            selectedDate={departureDate}
            onDateChange={handleStripDateChange}
            loading={isRepricing}
          />
        </div>
      )}

      {/* ── Fare Calendar Toggle + Panel ── */}
      {effectiveUserOrigin && effectiveUserDestination && (
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 pt-4">
          <button
            onClick={() => setShowFareCalendar((prev) => !prev)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition border ${
              showFareCalendar
                ? "bg-primary text-white border-primary"
                : "bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary"
            }`}
          >
            <FaCalendarAlt size={12} />
            {showFareCalendar ? "Hide Fare Calendar" : "Fare Calendar"}
          </button>
          {showFareCalendar && (
            <div className="mt-3">
              <FareCalendar
                origin={effectiveUserOrigin}
                destination={effectiveUserDestination}
                initialDate={typeof departureDate === "string" ? departureDate : undefined}
                tripType={tripType === "roundtrip" ? "roundtrip" : "oneway"}
                lengthOfStay={tripType === "roundtrip" && returnDate && departureDate
                  ? Math.max(1, Math.round((new Date(String(returnDate)).getTime() - new Date(String(departureDate)).getTime()) / 86400000))
                  : undefined
                }
              />
            </div>
          )}
        </div>
      )}

      {/* ── Main content ── */}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-5">
        <div className="flex gap-5">

          {/* ── Desktop sidebar ── */}
          <aside
            className="hidden sm:block w-[260px] shrink-0 sticky self-start z-40 overflow-y-auto rounded-xl"
            style={{ top: `${stickyTopPx}px`, maxHeight: `calc(100vh - ${stickyTopPx}px - 16px)` }}
          >
            {/* Sidebar header */}
            <div className="bg-primary rounded-t-xl px-4 py-3 flex items-center justify-between sticky top-0 z-10">
              <span className="text-white font-semibold text-sm flex items-center gap-2">
                <FaSlidersH size={13} /> Filters
              </span>
              {activeFilterCount > 0 && (
                <span className="bg-orange-400 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                  {activeFilterCount} active
                </span>
              )}
            </div>
            <div className="bg-white rounded-b-xl border border-t-0 border-gray-100 shadow-sm">
              <FlightFilterSidebar
                selectedStops={selectedStops}
                setSelectedStops={setSelectedStops}
                selectedDepartureTimes={selectedDepartureTimes}
                setSelectedDepartureTimes={setSelectedDepartureTimes}
                selectedAirlines={selectedAirlines}
                setSelectedAirlines={setSelectedAirlines}
                duration={duration}
                setDuration={setDuration}
                airlines={availableAirlines}
              />
            </div>
          </aside>

          {/* ── Results column ── */}
          <div className="flex-1 min-w-0 space-y-4">

            {/* ── Sort + results meta bar ── */}
            <div
              className="sticky z-50 bg-[#F2F6FF] pt-0 pb-2"
              style={{ top: isMobile ? `${mobileStickyTopPx}px` : `${stickyTopPx}px` }}
            >
              {/* Results count */}
              <div className="flex items-center justify-between mb-2.5">
                <div className="text-sm text-gray-600">
                  <span className="font-bold text-gray-900 text-base">
                    {flightsToDisplay.length}
                  </span>{" "}
                  {flightsToDisplay.length === 1 ? "flight" : "flights"} found
                  {activeFilterCount > 0 && (
                    <span className="ml-1.5 text-primary text-xs">
                      · {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""} applied
                    </span>
                  )}
                </div>
                {sortOption !== "none" && (
                  <button
                    onClick={() => setSortOption("none")}
                    className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                  >
                    <FaTimes size={10} /> Clear sort
                  </button>
                )}
              </div>

              {/* Sort tiles */}
              <div className="flex items-center gap-2.5 overflow-x-auto pb-1">
                <SortTile
                  id="cheapest"
                  icon={<FaTag size={13} />}
                  label="Cheapest"
                  subLabel={cheapestMeta ? formatFare(cheapestMeta.amount, cheapestMeta.currency) : "-"}
                  active={sortOption === "cheapest"}
                  onClick={() => setSortOption((p) => (p === "cheapest" ? "none" : "cheapest"))}
                />
                <SortTile
                  id="fastest"
                  icon={<FaBolt size={13} />}
                  label="Fastest"
                  subLabel={fastestMeta ? formatFare(fastestMeta.amount, fastestMeta.currency) : "-"}
                  active={sortOption === "fastest"}
                  onClick={() => setSortOption((p) => (p === "fastest" ? "none" : "fastest"))}
                />
                <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-500 shrink-0">
                  <FaSort size={11} />
                  <span className="hidden sm:inline">
                    {sortOption === "none" ? "Default order" : sortOption === "cheapest" ? "Cheapest first" : "Fastest first"}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Flight cards ── */}
            {flightsToDisplay.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                  <FaPlane className="text-primary text-2xl" />
                </div>
                <div className="font-semibold text-gray-800 text-lg mb-1">No flights found</div>
                <div className="text-gray-500 text-sm max-w-xs">
                  Try adjusting your filters or search for different dates.
                </div>
                {activeFilterCount > 0 && (
                  <button
                    onClick={() => {
                      setSelectedStops([]);
                      setSelectedDepartureTimes([]);
                      setSelectedAirlines([]);
                      setShowDirectOnly(false);
                    }}
                    className="mt-4 text-primary border border-primary px-4 py-2 rounded-lg text-sm hover:bg-blue-50 transition-colors"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            ) : (
              (() => {
                const groups = new Map<number, any[]>();
                flightsToDisplay.forEach((f) => {
                  const gi = f.groupIndex ?? 0;
                  if (!groups.has(gi)) groups.set(gi, []);
                  groups.get(gi)?.push(f);
                });

                return Array.from(groups.entries()).map(([gi, flights]) => {
                  const firstFlight = flights[0];
                  const firstFlightLegs: any[] = firstFlight?.legs ?? [];
                  const firstOutboundLeg =
                    firstFlightLegs.find((l: any) => l.direction === "outbound" || l.direction === "segment_1") ??
                    firstFlightLegs[0];
                  const groupLeg = {
                    departureLocation: (firstOutboundLeg?.departure?.airport?.code || "").toString().toUpperCase(),
                    arrivalLocation: (firstOutboundLeg?.arrival?.airport?.code || "").toString().toUpperCase(),
                  };
                  const alt = shouldShowAlternateItinerary(
                    effectiveUserOrigin, effectiveUserDestination, groupLeg
                  );

                  return (
                    <div key={`group-${gi}`} className="space-y-3">
                      {alt.show && (
                        <div className="flex items-center gap-3 my-1">
                          <div className="flex-1 h-px bg-gray-200" />
                          <div className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            {alt.text}
                          </div>
                          <div className="flex-1 h-px bg-gray-200" />
                        </div>
                      )}

                      {flights.map((flight: any, idx: number) => {
                        const key = `g${gi}-f${flight.id}-${flight._origIndex ?? idx}`;
                        const airlineCode = flight.validatingCarrier?.code ?? flight.validatingCarrier ?? "";
                        const amount = flight.pricingInfo?.total;
                        const baseFare = flight.pricingInfo?.baseFare;
                        const taxes = flight.pricingInfo?.taxes;
                        const currency = flight.pricingInfo?.currency;
                        const flightLegs: any[] = flight.legs ?? [];
                        const outboundLeg = flightLegs.find((l: any) => l.direction === "outbound") ?? flightLegs[0];
                        const inboundLeg = flightLegs.find((l: any) => l.direction === "inbound");
                        const onward = outboundLeg?.segments?.[0];
                        const returnFlight = inboundLeg?.segments?.[0];

                        if (tripType === "multicity" || flight.tripType === "multicity") {
                          return (
                            <div key={key} className="rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                              <MultiCityFlightCard
                                itinerary={flight}
                                showFareSelection={true}
                                showBookButton={true}
                                classOfService={classOfService}
                                selectedClass={selectedClass}
                              />
                            </div>
                          );
                        } else if (tripType === "oneway") {
                          const onwardSegs = outboundLeg?.segments ?? [];
                          const firstOnward = onwardSegs[0];
                          return (
                            <div key={key} className="rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                              <FlightCard
                                onward={{
                                  flightNumber: `${firstOnward?.marketingCarrier?.code ?? ""} ${firstOnward?.marketingCarrier?.flightNumber ?? ""}`,
                                  airlineName: airlineCode,
                                  airlineLogo: `/assets/airlines/${airlineCode}.png`,
                                  from: outboundLeg?.departure?.airport?.code ?? "-",
                                  to: outboundLeg?.arrival?.airport?.code ?? "-",
                                  departure: parseTimeFromISO(outboundLeg?.departure?.dateTime),
                                  arrival: parseTimeFromISO(outboundLeg?.arrival?.dateTime),
                                }}
                                amount={amount}
                                currency={currency}
                                baseFare={baseFare}
                                taxes={taxes}
                                classType={classOfService}
                                selectedClass={selectedClass}
                                fareTiers={
                                  Array.isArray(flight.fareOptions)
                                    ? flight.fareOptions.map((fo: any) => mapFareOption(fo, onward))
                                    : []
                                }
                                flightData={flight}
                              />
                            </div>
                          );
                        } else {
                          return (
                            <div key={key} className="rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                              {(() => {
                                const onwardCarrierCode =
                                  onward?.marketingCarrier?.code ??
                                  onward?.operatingCarrier?.code ??
                                  airlineCode;
                                const returnCarrierCode =
                                  returnFlight?.marketingCarrier?.code ??
                                  returnFlight?.operatingCarrier?.code ??
                                  airlineCode;
                                return (
                                  <RoundTripFlightCard
                                    amount={amount}
                                    currency={currency}
                                    baseFare={baseFare}
                                    taxes={taxes}
                                    classOfService={classOfService}
                                    selectedClass={selectedClass}
                                    flightData={flight}
                                    onward={{
                                      flightNumber: `${onward?.marketingCarrier?.code ?? ""} ${onward?.marketingCarrier?.flightNumber ?? ""}`,
                                      airlineName: onwardCarrierCode,
                                      airlineLogo: `/assets/airlines/${onwardCarrierCode}.png`,
                                      from: outboundLeg?.departure?.airport?.code ?? "-",
                                      to: outboundLeg?.arrival?.airport?.code ?? "-",
                                      departure: parseTimeFromISO(outboundLeg?.departure?.dateTime),
                                      arrival: parseTimeFromISO(outboundLeg?.arrival?.dateTime),
                                    }}
                                    returnSegment={{
                                      flightNumber: `${returnFlight?.marketingCarrier?.code ?? ""} ${returnFlight?.marketingCarrier?.flightNumber ?? ""}`,
                                      airlineName: returnCarrierCode,
                                      airlineLogo: `/assets/airlines/${returnCarrierCode}.png`,
                                      from: inboundLeg?.departure?.airport?.code ?? "-",
                                      to: inboundLeg?.arrival?.airport?.code ?? "-",
                                      departure: parseTimeFromISO(inboundLeg?.departure?.dateTime),
                                      arrival: parseTimeFromISO(inboundLeg?.arrival?.dateTime),
                                    }}
                                  />
                                );
                              })()}
                            </div>
                          );
                        }
                      })}
                    </div>
                  );
                });
              })()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightResults;